import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Sparkles, ShieldCheck } from 'lucide-react';
import { fetchTreatments, getSlug } from '../lib/api';
import { Helmet } from 'react-helmet-async';

const BOOKING_LINK = "https://system.easypractice.net/book/akupunktur-klinikken-acu-sense";

export default function Treatments() {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTreatments() {
      try {
        const data = await fetchTreatments();
        setTreatments(data);
      } catch (error) {
        console.error('Error loading treatments:', error);
      } finally {
        setLoading(false);
      }
    }
    loadTreatments();
  }, []);

  const mainTreatments = treatments.filter(t => !t.is_seasonal);
  const seasonalTreatments = treatments.filter(t => t.is_seasonal);

  return (
    <>
      <Helmet>
        <title>Behandlinger | Klinik Acu-sense</title>
        <meta name="description" content="Udforsk professionelle akupunkturbehandlinger i Hammel. Jeg tilbyder Seizen Hari, klassisk akupunktur, øreakupunktur, Auriculo, posturologi samt sæsonbehandlinger." />
        <meta property="og:title" content="Behandlinger | Klinik Acu-sense" />
        <meta property="og:description" content="Udforsk professionelle akupunkturbehandlinger i Hammel. Jeg tilbyder Seizen Hari, klassisk akupunktur, øreakupunktur, Auriculo, posturologi samt sæsonbehandlinger." />
        <link rel="canonical" href="https://www.acu-sense.dk/behandlinger" />
      </Helmet>

      {/* Page Header */}
      <section className="bg-[#F3F1EC] py-16 border-b border-[#E6E4DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C3531] tracking-tight">
            Behandlinger & Specialer
          </h1>
          <p className="text-base sm:text-lg text-[#5C6360] max-w-2xl mx-auto leading-relaxed">
            Hos Klinik Acu-sense tilbyder jeg skræddersyede behandlingsforløb, der tager udgangspunkt i din krops unikke ubalancer. Jeg kombinerer dyb akupunkturviden med neurofysioterapeutisk indsigt.
          </p>
        </div>
      </section>

      {/* Main Treatments Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531] tracking-tight">
              Hovedbehandlinger
            </h2>
            <div className="h-1 w-20 bg-[#5F7464] rounded-full mt-3" />
          </div>

          <div className="space-y-16">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-[#5F7464] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              mainTreatments.map((treatment, idx) => (
                <div
                  key={treatment.id || treatment.slug}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                    idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image */}
                  <div className={`lg:col-span-5 ${idx % 2 === 1 ? 'lg:order-last' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-md border border-[#E6E4DF] h-[320px] sm:h-[380px]">
                      <img
                        src={treatment.image?.[0] || "https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/c9b695e4-28b7-4bf4-8745-14a1efd7932e.webp"}
                        alt={treatment.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Text Content */}
                  <div className="lg:col-span-7 space-y-5">
                    <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531]">
                      {treatment.title}
                    </h3>
                    <div
                      className="text-sm sm:text-base text-[#5C6360] leading-relaxed space-y-4"
                      dangerouslySetInnerHTML={{ __html: treatment.description }}
                    />
                    <div className="pt-4 flex flex-wrap items-center gap-4">
                      <a
                        href={BOOKING_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#5F7464] text-white text-sm font-medium hover:bg-[#4E6052] transition-colors duration-200 shadow-sm"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Book tid online
                      </a>
                      <Link
                        to={`/behandlinger/${getSlug(treatment)}`}
                        className="inline-flex items-center text-sm font-semibold text-[#5F7464] hover:text-[#4E6052] transition-colors duration-200"
                      >
                        Læs mere om behandlingen
                        <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Seasonal Treatments Section */}
      {seasonalTreatments.length > 0 && (
        <section className="py-20 bg-[#FAF9F6] border-t border-b border-[#E6E4DF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-12">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#5F7464]/10 text-[#3D4F44] text-xs font-bold tracking-wider uppercase mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                Aktuelle sæsonbehandlinger
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531] tracking-tight">
                Sæsonrelevante ydelser
              </h2>
              <p className="text-sm sm:text-base text-[#5C6360] mt-2">
                Jeg tilbyder særlige behandlinger tilpasset årstidens udfordringer, herunder effektiv allergi- og myggebehandling.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
              {seasonalTreatments.map((treatment) => (
                <div
                  key={treatment.id || treatment.slug}
                  className="bg-white rounded-2xl border border-[#E6E4DF] overflow-hidden shadow-sm flex flex-col justify-between"
                >
                  <div>
                    <div className="h-52 relative overflow-hidden">
                      <img
                        src={treatment.image?.[0] || "https://assets.auxodev.dk/assets/2c4bccc2-b14e-4d92-a991-09327bae6f21/3c35f7cd-14a5-431c-9e61-3582fad6e704.webp"}
                        alt={treatment.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 left-4 bg-[#5F7464] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                        Sæsonrelevant
                      </div>
                    </div>
                    <div className="p-6 space-y-3">
                      <h3 className="font-serif text-xl font-bold text-[#2C3531]">
                        {treatment.title}
                      </h3>
                      <div
                        className="text-sm text-[#5C6360] leading-relaxed line-clamp-4"
                        dangerouslySetInnerHTML={{ __html: treatment.description }}
                      />
                    </div>
                  </div>
                  <div className="p-6 pt-0">
                    <div className="pt-4 border-t border-[#F3F1EC] flex items-center justify-between gap-4">
                      <span className="text-xs font-bold text-[#5F7464] uppercase tracking-wider">
                        {treatment.price}
                      </span>
                      <Link
                        to={`/behandlinger/${getSlug(treatment)}`}
                        className="inline-flex items-center text-sm font-semibold text-[#5F7464] hover:text-[#4E6052] transition-colors duration-200"
                      >
                        Læs mere
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust Badge Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="w-12 h-12 rounded-full bg-[#8FA89B]/10 flex items-center justify-center mx-auto text-[#5F7464]">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#2C3531]">
            Er du i tvivl om, hvilken behandling der passer til dig?
          </h2>
          <p className="text-sm sm:text-base text-[#5C6360] leading-relaxed max-w-2xl mx-auto">
            Du er altid velkommen til at kontakte klinikken for en uforpligtende snak, eller du kan booke en gratis 30-minutters undersøgelse online, hvor vi sammen kan kortlægge dine behov.
          </p>
          <div className="pt-4">
            <a
              href={BOOKING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#5F7464] text-white font-medium tracking-wide hover:bg-[#4E6052] transition-colors duration-200 shadow-md"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book gratis undersøgelse
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
